"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const NZ_REGIONS = [
  "Northland",
  "Auckland",
  "Waikato",
  "Bay of Plenty",
  "Gisborne",
  "Hawke's Bay",
  "Taranaki",
  "Manawatū–Whanganui",
  "Wellington",
  "Tasman",
  "Nelson",
  "Marlborough",
  "West Coast",
  "Canterbury",
  "Otago",
  "Southland",
] as const;

const PRODUCT_TYPES = [
  "Hand-drawn / painted portraits",
  "Photographic portraits",
  "Urns",
  "Native-timber boxes",
  "Jewellery (incl. fur lockets)",
  "Ceramics",
  "Sculpture",
  "Other",
] as const;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ArtistsPageClient() {
  const [formData, setFormData] = useState({
    name: "",
    studioName: "",
    email: "",
    region: "",
    portfolioUrl: "",
    materials: "",
  });
  const [productTypes, setProductTypes] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleProductType = (type: string, checked: boolean) => {
    setProductTypes((prev) =>
      checked ? [...prev, type] : prev.filter((t) => t !== type),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.region ||
      !formData.portfolioUrl.trim()
    ) {
      setError("Please fill in all required fields.");
      return;
    }
    if (!EMAIL_REGEX.test(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    try {
      const parsed = new URL(formData.portfolioUrl);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        throw new Error("Invalid protocol");
      }
    } catch {
      setError(
        "Portfolio link must be a valid URL starting with https:// (or http://).",
      );
      return;
    }
    if (productTypes.length === 0) {
      setError("Please select at least one product type.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/partners/artists", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, productTypes }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to submit");
      }
      setIsSubmitted(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to submit. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link href="/" className="relative">
              <Image
                src="/icons/logo-rgb.svg"
                alt="PetAftercare"
                className="w-[250px] md:w-[200px] h-[60px] md:h-[50px] sm:h-auto"
                width={250}
                height={60}
                priority
              />
            </Link>
            <Link
              href="/"
              className="text-[#004da9] hover:text-[#002e65] font-medium transition-colors"
            >
              ← Home
            </Link>
          </div>
        </div>
      </div>

      <div className="min-h-screen bg-[#faf6fa] border border-t-[#979797]">
        <div className="container py-12">
          {/* Hero */}
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-block text-[13px] font-bold tracking-[0.2em] uppercase text-[#004da9] mb-4">
              For Artists & Makers
            </span>
            <h1 className="text-[50px] xl:text-[40px] md:text-[30px] sm:text-[26px] leading-[1.2] text-[#002e65] font-[600] mb-6">
              For New Zealand Artists &amp; Makers
            </h1>
            <p className="text-[20px] xl:text-[18px] md:text-[16px] leading-[1.6] text-[#3a5a7c]">
              Every keepsake in our catalogue is made in New Zealand. We
              don&apos;t import from offshore marketplaces — we work with local
              studios whose craft meets a standard worthy of the families we
              serve. If you create work that fits — original portraits,
              native-timber boxes, ceramic urns, jewellery, photography —
              we&apos;d like to hear from you.
            </p>
          </div>

          {/* How it works */}
          <div className="max-w-3xl mx-auto mb-12">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-[#002e65] h-3 w-full"></div>
              <div className="p-[25px] md:p-[20px]">
                <h2 className="text-[24px] md:text-[20px] font-bold text-[#002e65] mb-6">
                  How it works
                </h2>
                <ol className="space-y-4">
                  {[
                    "Send us a link to your portfolio and what you make.",
                    "We review work for craftsmanship, materials, and catalogue fit.",
                    "Approved makers are presented to families during their service — you set your own pricing and lead times.",
                  ].map((step, i) => (
                    <li key={i} className="flex gap-4 items-start">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#004da9] text-white font-bold flex items-center justify-center text-sm">
                        {i + 1}
                      </span>
                      <p className="text-[16px] text-[#3a5a7c] leading-[1.6] pt-1">
                        {step}
                      </p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-[#004da9] h-3 w-full"></div>
              <div className="p-[25px] md:p-[20px]">
                {!isSubmitted ? (
                  <>
                    <h2 className="text-[28px] md:text-[24px] font-bold text-[#004da9] mb-6 text-center">
                      Register your interest
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label
                            htmlFor="name"
                            className="text-[#004da9] font-medium mb-2 block"
                          >
                            Your name *
                          </Label>
                          <Input
                            id="name"
                            name="name"
                            type="text"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full border-[#004da9] focus:ring-[#004da9] focus:border-[#004da9]"
                            placeholder="Your name"
                          />
                        </div>
                        <div>
                          <Label
                            htmlFor="studioName"
                            className="text-[#004da9] font-medium mb-2 block"
                          >
                            Studio or business name
                          </Label>
                          <Input
                            id="studioName"
                            name="studioName"
                            type="text"
                            value={formData.studioName}
                            onChange={handleChange}
                            className="w-full border-[#004da9] focus:ring-[#004da9] focus:border-[#004da9]"
                            placeholder="Optional"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label
                            htmlFor="email"
                            className="text-[#004da9] font-medium mb-2 block"
                          >
                            Email *
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full border-[#004da9] focus:ring-[#004da9] focus:border-[#004da9]"
                            placeholder="you@example.co.nz"
                          />
                        </div>
                        <div>
                          <Label
                            htmlFor="region"
                            className="text-[#004da9] font-medium mb-2 block"
                          >
                            Region *
                          </Label>
                          <select
                            id="region"
                            name="region"
                            required
                            value={formData.region}
                            onChange={handleChange}
                            className="flex h-10 w-full items-center justify-between rounded-md border border-[#004da9] bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#004da9] focus:border-[#004da9]"
                          >
                            <option value="" disabled>
                              Select your region
                            </option>
                            {NZ_REGIONS.map((region) => (
                              <option key={region} value={region}>
                                {region}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <Label
                          htmlFor="portfolioUrl"
                          className="text-[#004da9] font-medium mb-2 block"
                        >
                          Portfolio link *
                        </Label>
                        <Input
                          id="portfolioUrl"
                          name="portfolioUrl"
                          type="url"
                          required
                          value={formData.portfolioUrl}
                          onChange={handleChange}
                          className="w-full border-[#004da9] focus:ring-[#004da9] focus:border-[#004da9]"
                          placeholder="https://your-website.co.nz or instagram.com/yourstudio"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          Website, Instagram, Facebook, Etsy, or any page where
                          we can see your work.
                        </p>
                      </div>

                      <div>
                        <span className="text-[#004da9] font-medium mb-2 block">
                          Product types *
                        </span>
                        <p className="text-xs text-gray-500 mb-3">
                          Select everything that applies.
                        </p>
                        <div className="grid sm:grid-cols-1 grid-cols-2 gap-3">
                          {PRODUCT_TYPES.map((type) => {
                            const id = `pt-${type}`;
                            const checked = productTypes.includes(type);
                            return (
                              <div
                                key={type}
                                className="flex items-start space-x-2"
                              >
                                <Checkbox
                                  id={id}
                                  checked={checked}
                                  onCheckedChange={(c) =>
                                    toggleProductType(type, c === true)
                                  }
                                  className="mt-1"
                                />
                                <label
                                  htmlFor={id}
                                  className="text-sm text-[#002e65] leading-snug cursor-pointer"
                                >
                                  {type}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <Label
                          htmlFor="materials"
                          className="text-[#004da9] font-medium mb-2 block"
                        >
                          Materials you work in
                        </Label>
                        <Input
                          id="materials"
                          name="materials"
                          type="text"
                          value={formData.materials}
                          onChange={handleChange}
                          className="w-full border-[#004da9] focus:ring-[#004da9] focus:border-[#004da9]"
                          placeholder="e.g. rimu, pohutukawa, pounamu, sterling silver, stoneware"
                        />
                      </div>

                      {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-sm">
                          {error}
                        </div>
                      )}

                      <div className="flex justify-end">
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="bg-[#004da9] hover:bg-[#003d87] text-white px-8 py-6 text-base rounded-full"
                        >
                          {isSubmitting ? "Submitting…" : "Register interest"}
                        </Button>
                      </div>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <div className="mb-6">
                      <svg
                        className="w-16 h-16 mx-auto text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-[#004da9] mb-4">
                      Thanks — we&apos;ll be in touch.
                    </h3>
                    <p className="text-gray-700 text-lg mb-2">
                      We&apos;ll review your portfolio and respond within a few
                      weeks.
                    </p>
                    <p className="text-gray-600 mb-6">
                      If you&apos;d like to reach us in the meantime,{" "}
                      <Link
                        href="/contact"
                        className="text-[#004da9] hover:text-[#003d87] font-semibold underline"
                      >
                        contact us here
                      </Link>
                      .
                    </p>
                    <Link
                      href="/"
                      className="inline-block bg-[#004da9] hover:bg-[#003d87] text-white px-6 py-3 rounded-full font-medium"
                    >
                      Back to Home
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
